// =============================================================================
// Orders — buyer-facing order list + right-pane tracking detail.
// Slim composer. Row components, the tracking pane, the receipt-confirm
// and review modals, plus shared status/payment constants live in ./orders/.
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../lib/theme";
import { Eyebrow } from "../components/Eyebrow";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { supabase } from "../lib/supabase";
import { logChannelStatus } from "../lib/realtimeStatus";
import { OrderRow, PastRow } from "./orders/OrderRow";
import { Tracking } from "./orders/Tracking";

export function Orders({ user, onMessageSeller, openSubOrderId, onOpenedSubOrder }) {
  const queryClient = useQueryClient();
  const [selId, setSelId] = useState(null);

  // Deep-link from a chat order-ref card. Run BEFORE the most-recent default
  // so we don't briefly select the wrong row.
  useEffect(() => {
    if (!openSubOrderId) return;
    setSelId(openSubOrderId);
    onOpenedSubOrder?.();
  }, [openSubOrderId, onOpenedSubOrder]);

  // Cached via TanStack Query — tab revisits render instantly. The realtime
  // channel below invalidates on any sub_order change so status updates land
  // without a manual refresh.
  const { data: subOrders = [], isLoading: loading } = useQuery({
    queryKey: ["buyer-orders", user?.id],
    enabled:  !!user?.id,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("sub_orders")
        .select(
          "id, order_id, seller_id, sub_number, status, total, subtotal, delivery_fee, payment_method, delivery_method, delivery_address, created_at, updated_at, shipped_at, delivered_at, sellers(store_name, logo_url), orders!inner(buyer_id, order_number), order_items(id, product_name, product_image, quantity, unit_price, size, product_id, products(title, images))"
        )
        .eq("orders.buyer_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30);
      return data || [];
    },
  });

  // Default the right-pane selection to the most recent order whenever the
  // list changes and the previously-selected one is gone.
  useEffect(() => {
    if (!subOrders.length) return;
    if (!selId || !subOrders.some((o) => o.id === selId)) {
      setSelId(subOrders[0].id);
    }
  }, [subOrders, selId]);

  useEffect(() => {
    if (!user?.id) return;
    const channelName = `buyer-sub-orders-${user.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sub_orders" },
        () => queryClient.invalidateQueries({ queryKey: ["buyer-orders", user.id] })
      )
      .subscribe(logChannelStatus(channelName));
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const active = useMemo(
    () =>
      subOrders.filter(
        (o) => !["delivered", "completed", "cancelled", "refunded"].includes(o.status)
      ),
    [subOrders]
  );
  const past = useMemo(
    () =>
      subOrders.filter(
        (o) => ["delivered", "completed", "cancelled", "refunded"].includes(o.status)
      ),
    [subOrders]
  );
  const sel = useMemo(
    () => subOrders.find((o) => o.id === selId) || subOrders[0],
    [subOrders, selId]
  );

  if (loading) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <Eyebrow>Orders</Eyebrow>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>My orders</h1>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 480px", gap: 18 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} h={100} r={16} />
            ))}
          </div>
          <Skeleton h={400} r={16} />
        </div>
      </div>
    );
  }

  if (!subOrders.length) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <Eyebrow>Orders</Eyebrow>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>My orders</h1>
        </div>
        <EmptyState
          icon="package"
          title="No orders yet"
          body="Once you place an order, tracking will appear here with real-time delivery status."
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <Eyebrow>Orders · {active.length} active</Eyebrow>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", letterSpacing: "-0.6px" }}>
          My orders
        </h1>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 480px", gap: 18, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {active.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 4px 10px" }}>
                <div
                  className="pulse"
                  style={{ width: 8, height: 8, borderRadius: "50%", background: C.hunter }}
                />
                <h3 style={{ fontSize: 13, fontWeight: 800, margin: 0, letterSpacing: "0.3px" }}>
                  ACTIVE
                </h3>
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{active.length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {active.map((o) => (
                  <OrderRow key={o.id} o={o} selected={sel?.id === o.id} onSelect={() => setSelId(o.id)} />
                ))}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h3
                style={{
                  fontSize:      13,
                  fontWeight:    800,
                  margin:        "0 4px 10px",
                  letterSpacing: "0.3px",
                  color:         C.muted,
                }}
              >
                PAST ORDERS
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {past.map((o) => (
                  <PastRow key={o.id} o={o} onSelect={() => setSelId(o.id)} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            position:           "sticky",
            top:                82,
            maxHeight:          "calc(100vh - 100px)",
            overflowY:          "auto",
            overscrollBehavior: "contain",
          }}
        >
          {sel && <Tracking sel={sel} user={user} onMessageSeller={onMessageSeller} />}
        </div>
      </div>
    </div>
  );
}
